import { type PropsWithChildren, type FunctionComponent, useState, useMemo } from 'react'
import { colors, useBooleanState } from '@retailer-platform/shared-components'
import { useRouteMatch } from 'react-router'
import { URL_APP_ADMIN } from '../../../../utils/routing/routes'
import { NavContext } from './NavContext'
import { type NavContextValue } from './nav.types'

interface NavProviderProps {}

const navStyleContext: Record<
  string,
  {
    readonly backgroundColor: string
    readonly contentColor: string
    readonly iconColor: 'systemGrayscale00' | 'systemGrayscale50'
  }
> = {
  'retailer-scoped': {
    backgroundColor: colors.GRAYSCALE.X70,
    contentColor: colors.GRAYSCALE.X0,
    iconColor: 'systemGrayscale00',
  },
  admin: {
    backgroundColor: colors.GRAYSCALE.X10,
    contentColor: colors.GRAYSCALE.X50,
    iconColor: 'systemGrayscale50',
  },
  'dev-portal': {
    backgroundColor: colors.GRAYSCALE.X0,
    contentColor: colors.GRAYSCALE.X70,
    iconColor: 'systemGrayscale50',
  },
  'retailer-scoped-v2': {
    backgroundColor: colors.GRAYSCALE.X70,
    contentColor: colors.GRAYSCALE.X0,
    iconColor: 'systemGrayscale00',
  },
  'admin-v2': {
    backgroundColor: colors.GRAYSCALE.X10,
    contentColor: colors.GRAYSCALE.X50,
    iconColor: 'systemGrayscale50',
  },
}

export const NavProvider: FunctionComponent<PropsWithChildren<NavProviderProps>> = ({
  children,
}) => {
  const [isNavOpen, openNav, closeNav] = useBooleanState(false)
  const [folderControlledByTour, setFolderControlledByTour] = useState('')
  const [isBlockedByModal, setIsBlockedByModal] = useState(false)
  const [isNavVisible, showNav, hideNav] = useBooleanState(true)
  const [isSideNavVisible, showSideNav, hideSideNav] = useBooleanState(true)

  const [mostRecentClickedRoute, setMostRecentClickedRoute] = useState(null)

  const adminMatch = useRouteMatch(URL_APP_ADMIN)
  const getNavType = () => {
    if (adminMatch) return 'admin-v2'
    return 'retailer-scoped-v2'
  }
  const navType = getNavType()

  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  const contextValue: NavContextValue = useMemo(
    () => ({
      isNavOpen,
      openNav,
      closeNav,
      folderControlledByTour,
      setFolderControlledByTour,
      isBlockedByModal,
      setIsBlockedByModal,
      mostRecentClickedRoute,
      setMostRecentClickedRoute,
      isNavVisible,
      showNav,
      hideNav,
      isSideNavVisible,
      showSideNav,
      hideSideNav,
      ...navStyleContext[navType],
    }),
    [
      isNavOpen,
      openNav,
      closeNav,
      folderControlledByTour,
      setFolderControlledByTour,
      isBlockedByModal,
      setIsBlockedByModal,
      mostRecentClickedRoute,
      setMostRecentClickedRoute,
      isNavVisible,
      showNav,
      hideNav,
      isSideNavVisible,
      showSideNav,
      hideSideNav,
      navType,
    ]
  )

  return <NavContext.Provider value={contextValue}>{children}</NavContext.Provider>
}
